import {CryptoParameter,singSize} from "./keyEx";
import {bigintToBuf} from "bigint-conversion";

export class cryptoPacket{
    private webcrypt:Crypto;
    private cyptParams:CryptoParameter
    private keyPair?:CryptoKeyPair;
    private peerKey?:CryptoKey;
    private deriveryKey?:CryptoKey;
    private publicKeyJwk?:JsonWebKey;
    private encCounter:bigint;
    private secretShare:Uint8Array;
    private bitCal:intArrayBitCal;

    constructor(cryptoParameter:CryptoParameter){
        this.webcrypt = globalThis.crypto as Crypto||new Crypto();
        this.cyptParams = cryptoParameter;
        this.encCounter = 0n;
        this.secretShare = new Uint8Array(cryptoParameter.dataCrypto.vectorSize);
        this.bitCal = new intArrayBitCal(true,{"safeArg":true});
    }

    public getRandomValues(array:Uint8Array|null){
        return this.webcrypt.getRandomValues(array);
    }

    public async generateKeyPair(){
        this.keyPair = await this.webcrypt.subtle.generateKey(this.cyptParams.public.cryptParams,true,this.cyptParams.public.usage) as CryptoKeyPair;
        if(this.keyPair.publicKey && this.keyPair.privateKey){
            return this.publicKeyJwk = await this.webcrypt.subtle.exportKey("jwk",this.keyPair.publicKey);
        }
    }

    public async generateSaverSingKey(){
        const _ = await this.webcrypt.subtle.generateKey(this.cyptParams.sign.keyAlgrithm,true,["sign","verify"]);
        if(_ && _.publicKey && _.privateKey){
            return {
                "public":await this.webcrypt.subtle.exportKey("jwk",_.publicKey),
                "private":await this.webcrypt.subtle.exportKey("jwk",_.privateKey),
            }
        }
    }

    public async createSign(privateKey:JsonWebKey,data:unknown){
        const key = await this.webcrypt.subtle.importKey("jwk",privateKey,this.cyptParams.sign.keyAlgrithm,true,["sign"]);
        const dataBufferSorce = ArrayBuffer.isView(data)?new Uint8Array(data.buffer):new TextEncoder().encode(JSON.stringify(data));
        const signRs = await this.webcrypt.subtle.sign(this.cyptParams.sign.signParam,key,dataBufferSorce);
        return ecSigToASN1Sig(signRs);
    }

    public async verifySign(publicKey:JsonWebKey,data:unknown,sing:Uint8Array){
        const key = await this.webcrypt.subtle.importKey("jwk",publicKey,this.cyptParams.sign.keyAlgrithm,false,["verify"]);
        const dataBufferSorce = ArrayBuffer.isView(data)?new Uint8Array(data.buffer):new TextEncoder().encode(JSON.stringify(data));
        const signSize = singSize(this.cyptParams.sign);
        if(!signSize){
            return false;
        }
        const ecSig = asn1toEcSig(sing,signSize);
        if(!ecSig){
            return false;
        }
        return await this.webcrypt.subtle.verify(this.cyptParams.sign.signParam,key,ecSig,dataBufferSorce);
    }

    public async importKey(jwk:JsonWebKey){
        this.peerKey = await this.webcrypt.subtle.importKey("jwk",jwk,this.cyptParams.public.cryptParams,false,[]);
        if(this.keyPair?.privateKey && this.peerKey){
            try{
                if(this.cyptParams.public.hash){
                    const sSecret = await this.webcrypt.subtle.deriveBits({
                        "name":this.cyptParams.public.algrithm,
                        "public":this.peerKey,
                    },this.keyPair.privateKey,this.cyptParams.dataCrypto.delivaryparam.length);
                    this.deriveryKey = await this.webcrypt.subtle.deriveKey(
                        {"name":"HKDF","hash":this.cyptParams.public.hash,"info":new Uint8Array(0),"salt":new Uint8Array(0)},
                        await this.webcrypt.subtle.importKey("raw",sSecret,{"name":"HKDF"},false,["deriveBits","deriveKey"]) ,
                        this.cyptParams.dataCrypto.delivaryparam,
                        false,
                        this.cyptParams.dataCrypto.usage)
                }else{
                    this.deriveryKey = await this.webcrypt.subtle.deriveKey({
                        "name":this.cyptParams.public.algrithm,
                        "public":this.peerKey,
                    },this.keyPair.privateKey,this.cyptParams.dataCrypto.delivaryparam,false,this.cyptParams.dataCrypto.usage);
                }
                const ss = await this.encrypt(new Uint8Array(this.cyptParams.dataCrypto.vectorSize));
                if(!ss){
                    return false;
                }
                this.encCounter = 0n;
                this.secretShare.set(ss.slice(ss.byteLength-this.secretShare.byteLength))
                return true;
            }catch(err){
                console.error(err)
                return false;
            }
        }
        return false;
    }

    public async encrypt(data:any){
        if(this.deriveryKey){
            const dataBufferSorce = data instanceof ArrayBuffer?new Uint8Array(data):ArrayBuffer.isView(data)?new Uint8Array(data.buffer):new TextEncoder().encode(JSON.stringify(data));
            const targetData = new Uint8Array(dataBufferSorce.byteLength+1);
            targetData.set(data instanceof ArrayBuffer||ArrayBuffer.isView(data)?[0]:[1],0);
            targetData.set(dataBufferSorce,1);
            const iv = new Uint8Array(this.cyptParams.dataCrypto.vectorSize);
            const cntDat = new Uint8Array(bigintToBuf(this.encCounter++));
            iv.set(this.bitCal.xor(cntDat,this.secretShare));
            const encryptBuffer = await this.webcrypt.subtle.encrypt({
                "name":this.cyptParams.dataCrypto.algrithm,
                "iv":iv,
            },this.deriveryKey,targetData);
            const buf = new Uint8Array(iv.byteLength + encryptBuffer.byteLength);
            buf.set(iv,0);
            buf.set(new Uint8Array(encryptBuffer),iv.byteLength);
            return buf;
        }
    }

    public async decrypt(data:Uint8Array){
        if(this.deriveryKey){
            if(typeof data === "undefined"){
                return;
            }
            if(data.length < this.cyptParams.dataCrypto.vectorSize){
                return null;
            }
            const iv = data.subarray(0,this.cyptParams.dataCrypto.vectorSize);
            const body = data.subarray(this.cyptParams.dataCrypto.vectorSize);
            try{
                const _buf = await this.webcrypt.subtle.decrypt({
                    "name":this.cyptParams.dataCrypto.algrithm,
                    "iv":iv,
                } as AesGcmParams,this.deriveryKey,new Uint8Array(body));
                const buf = new Uint8Array(_buf);
                const flag = buf.subarray(0,1);
                if(flag[0] === 0){
                    return buf.subarray(1);
                }else if(flag[0] === 1){
                    try{
                        return JSON.parse((new TextDecoder().decode(buf.subarray(1))));
                    }catch(err){console.error(err);}
                }
            }catch(err){
                console.error(err);
            }
        }
    }

    public hasDelivaryKey(){
        if(this.deriveryKey){
            return true;
        }else{
            return false;
        }
    }
    public getPublicKey(){
        if(this.publicKeyJwk){
            return this.publicKeyJwk;
        }else{
            return null;
        }
    }
    public getPublickeyJsonString(){
        try{
            return JSON.stringify(this.publicKeyJwk);
        }catch(err){
            return null;
        }
    }
}

const asn1toEcSig = (der:Uint8Array,keySize:number) => {
    if(der[0] !== 0x30){
        return;
    }
    const filedLen = getDerLen(der);
    const seq = der.subarray(2+filedLen[0],2+filedLen[0]+filedLen[1]);
    const rPointLen = getDerLen(seq);
    const rPointDat = paddingInt(seq.subarray(2+rPointLen[0],2+rPointLen[0]+rPointLen[1]),keySize);
    const nextSeq = seq.subarray(2+rPointLen[0]+rPointLen[1]);
    const sPointLen = getDerLen(nextSeq);
    const sPointDat = paddingInt(nextSeq.subarray(2+sPointLen[0],2+sPointLen[0]+sPointLen[1]),keySize);
    return new Uint8Array([...rPointDat,...sPointDat]);
}
const ecSigToASN1Sig = (sig:ArrayBufferLike) => {
    if(sig.byteLength % 2 != 0){
        return;
    }
    const rPointDat = trimArray(new Uint8Array(sig.slice(0,sig.byteLength/2)));
    const rPointLen = getASN1Len(rPointDat.byteLength);
    const rPointBlk = new Uint8Array(1+rPointLen.byteLength+rPointDat.byteLength);
    rPointBlk.set([0x02],0);
    rPointBlk.set(rPointLen,1);
    rPointBlk.set(rPointDat,1+rPointLen.byteLength);
    const sPointDat = trimArray(new Uint8Array(sig.slice(sig.byteLength/2)));
    const sPointLen = getASN1Len(sPointDat.byteLength);
    const sPointBlk = new Uint8Array(1+sPointLen.byteLength+sPointDat.byteLength);
    sPointBlk.set([0x02],0);
    sPointBlk.set(sPointLen,1);
    sPointBlk.set(sPointDat,1+sPointLen.byteLength);
    const seqLen = getASN1Len(rPointBlk.byteLength+sPointBlk.byteLength);
    const ans1Bytes = new Uint8Array(1+seqLen.byteLength+rPointBlk.byteLength+sPointBlk.byteLength);
    let seq = 0;
    ans1Bytes.set([0x30],seq);seq++;
    ans1Bytes.set(seqLen,seq);seq += seqLen.byteLength;
    ans1Bytes.set(rPointBlk,seq);seq += rPointBlk.byteLength;
    ans1Bytes.set(sPointBlk,seq);seq += sPointBlk.byteLength;
    return ans1Bytes;
}
const getASN1Len = (len:number) => {
    if(len<128){
        return new Uint8Array([len]);
    }else{
        const lenlen = Math.floor(len/256)+1;
        const lenBuf = new Uint8Array(1+lenlen);
        lenBuf[0] = 128+lenlen;
        for(let p=1;p<=lenlen;p++){
            lenBuf[p] = len&0xFF
            len = len >> 8;
        }
        return lenBuf;
    }
}
const getDerLen = (der:Uint8Array) => {
    if(der[1]<128){
        return [0,der[1]];
    }else{
        const lenlen = der[1] & 0x7f;
        const size = der.subarray(2,2+lenlen);
        const len = size.reverse().reduce((p,c,i)=>{
            p += c*(256**i);
            return p;
        },0);
        return [lenlen,len]
    }
}
const trimArray = (buf:Uint8Array) => {
    const _temp = [];
    let trim = false;
    for(let i=0;i<buf.byteLength;i++){
        if(buf[i] === 0x00){
            trim = true;
            continue;
        }
        _temp.push(...buf.subarray(i));
        break;
    }
    if(!trim || _temp[0] < 0x80){
        return new Uint8Array(_temp);
    }else{
        return new Uint8Array([0x00,..._temp]);
    }
}
const paddingInt = (uint:Uint8Array,sizeBit:number) => {
    const byteLen = Math.floor((sizeBit+7) / 8);
    const byteDat = new Uint8Array(byteLen);
    byteDat.set(uint,byteLen-uint.byteLength);
    return byteDat;
}

interface intArrayBitCalOptions{
    safeArg?:boolean
}
class intArrayBitCal{
    private endianBig:boolean;
    private options?:intArrayBitCalOptions

    constructor(endianBig:boolean,options?:intArrayBitCalOptions){
        this.endianBig = endianBig;
        this.options = options;
    }
    xor = (a:Uint8Array,b:Uint8Array)=>{
        const calA = this.options?.safeArg?a.subarray():a;
        const calB = this.options?.safeArg?b.subarray():b;
        const calLen = Math.max(calA.byteLength,calB.byteLength);
        const calBuf = new Uint8Array(calLen);
        const calOffset = calA.byteLength-calB.byteLength;
        if(this.endianBig){
            calA.reverse();
            calB.reverse();
        }
        for(let p=0;p<calBuf.byteLength;p++){
            calBuf[p] = (calA[p]||0) ^ (calB[p]||0);
        }
        if(this.endianBig){
            calBuf.reverse();
        }
        if(this.options?.safeArg && this.endianBig){
            calA.reverse();
            calB.reverse();
        }
        return calBuf;
    }
}